import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  PencilIcon, 
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  ArrowPathIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { saveAs } from 'file-saver';
import { parseISO, isValid, differenceInCalendarDays } from 'date-fns';

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
}

interface QuoteItem {
  id: number;
  structureId: number;
  startDate: string;
  endDate: string;
  unitPrice: number;      // Original price per unit (day/month)
  quantity?: number;       // Number of days or months
  totalPrice: number;     // unitPrice * quantity
  discountPercentage: number;
  finalPrice: number;       // totalPrice - (totalPrice * discountPercentage / 100)
  isMonthlyRate: boolean | null;
  notes?: string;
  Structure: Structure; // Assuming structure data is nested
}

interface Client {
  id: number;
  name: string;
  type?: string; // Optional type
  director?: string;
  phone?: string;
  email?: string;
  address?: string; // Basic address if available
}

interface Quote {
  id: number;
  quoteNumber: string;
  clientId: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted';
  totalAmount: number;     // Sum of all item totalPrice
  discountAmount: number;  // Sum of all item discount amounts
  finalAmount: number;     // Sum of all item finalPrice
  quoteDate: string;
  validUntil: string;
  notes?: string;
  termsAndConditions?: string;
  convertedToOrderId?: number;
  createdAt: string;
  updatedAt: string;
  Client: Client; // Assuming client data is nested
  QuoteItems: QuoteItem[]; // Assuming items are nested
}

const QuoteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [openingPrintView, setOpeningPrintView] = useState(false); // Renamed state

  // Fetch quote data
  useEffect(() => {
    const fetchQuoteDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/quotes/${id}`);
        setQuote(response.data);
      } catch (err) {
        console.error('Error fetching quote details:', err);
        setError('Ошибка при загрузке данных коммерческого предложения');
      } finally {
        setLoading(false);
      }
    };

    fetchQuoteDetails();
  }, [id]);
  
  // Handle quote conversion to order
  const handleConvertToOrder = async () => {
    if (!quote) return;
    
    if (window.confirm('Вы уверены, что хотите конвертировать это предложение в заказ?')) {
      setConverting(true);
      
      try {
        const response = await axios.post(`/quotes/${id}/convert`);
        
        // Redirect to the new order
        if (response.data.orderId) {
          window.location.href = `/orders/${response.data.orderId}`;
        }
      } catch (err) {
        console.error('Error converting quote to order:', err);
        alert('Ошибка при конвертации в заказ');
      } finally {
        setConverting(false);
      }
    }
  };

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!quote) return;
    setOpeningPrintView(true); // Indicate loading/opening state if needed
    setError(null);

    // Construct the URL for the print page
    const printUrl = `/quotes/${quote.id}/print`;

    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');

    if (!printWindow) {
      setError('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }

    // Reset the loading state after a short delay (optional)
    setTimeout(() => setOpeningPrintView(false), 1000);
  };

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) return 'Неверная дата';
      return new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return 'Неверная дата';
    }
  };
  
  // Format currency for display
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2 // Allow decimals for currency
    }).format(amount);
  };
  
  // Calculate duration in days (inclusive)
  const calculateDuration = (startDate: string, endDate: string): number | null => {
    if (!startDate || !endDate) return null;
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const durationDays = differenceInCalendarDays(end, start) + 1;
      if (!isValid(start) || !isValid(end) || durationDays < 1) {
        return null; // Return null for invalid duration
      }
       // Calculate the difference in days, inclusive of both start and end date
      return durationDays;
    } catch (error) {
      console.error('Error calculating duration:', error);
      return null;
    }
  };
  
  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'Черновик';
      case 'sent': return 'Отправлено';
      case 'accepted': return 'Принято';
      case 'rejected': return 'Отклонено';
      case 'expired': return 'Истекло';
      case 'converted': return 'Конвертировано';
      default: return status;
    }
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      case 'converted':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">{error}</p>
        <Link to="/quotes" className="mt-4 btn-primary inline-flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к списку
        </Link>
      </div>
    );
  }
  
  if (!quote) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-600">Коммерческое предложение не найдено</p>
        <Link to="/quotes" className="mt-4 btn-primary inline-flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к списку
        </Link>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader
        title={`Коммерческое предложение ${quote.quoteNumber || `КП-${quote.id}`}`}
        subtitle={`Статус: ${getStatusText(quote.status)}`}
        backLink="/quotes"
        actions={
          <div className="flex space-x-2">
            <button
              onClick={handleOpenPrintView} // Use new handler
              disabled={openingPrintView} // Use renamed state
              className="btn-outline flex items-center"
            >
              <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
              {openingPrintView ? 'Открытие...' : 'Печать'} {/* Update text */}
            </button>

            {/* Convert to Order Button */}
            {quote.status !== 'converted' && can('orders.create_from_quote') && (
              <button
                onClick={handleConvertToOrder}
                disabled={converting}
                className="btn-secondary flex items-center"
              >
                <ArrowPathIcon className="h-5 w-5 mr-1" />
                {converting ? 'Создание...' : 'Создать заказ'}
              </button>
            )}
            
            {/* Edit Button */}
            {quote.status === 'draft' && can('quotes.edit') && (
              <Link
                to={`/quotes/edit/${quote.id}`}
                className="btn-primary flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-1" />
                Редактировать
              </Link>
            )}
          </div>
        }
      />
      
      {/* Quote Metadata and Client Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="md:col-span-2 bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Детали предложения</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div><h3 className="detail-label">Номер КП</h3><p className="detail-value font-mono">{quote.quoteNumber || `КП-${quote.id}`}</p></div>
                  <div><h3 className="detail-label">Статус</h3><p><span className={`badge ${getStatusBadgeClass(quote.status)}`}>{getStatusText(quote.status)}</span></p></div>
                  <div><h3 className="detail-label">Дата КП</h3><p className="detail-value">{formatDate(quote.quoteDate)}</p></div>
                  <div><h3 className="detail-label">Действительно до</h3><p className="detail-value">{formatDate(quote.validUntil)}</p></div>
                  {quote.convertedToOrderId && (
                      <div><h3 className="detail-label">Конвертировано в заказ</h3><p className="detail-value"><Link to={`/orders/${quote.convertedToOrderId}`} className="text-primary hover:underline">Заказ #{quote.convertedToOrderId}</Link></p></div>
                  )}
                  <div><h3 className="detail-label">Создано</h3><p className="detail-value">{formatDate(quote.createdAt)}</p></div>
                  <div><h3 className="detail-label">Обновлено</h3><p className="detail-value">{formatDate(quote.updatedAt)}</p></div>
              </div>
          </div>
          <div className="md:col-span-1 bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold mb-4">Клиент</h2>
                {quote.Client ? (
                    <div className="space-y-2">
                        <p className="font-semibold text-lg"><Link to={`/clients/${quote.Client.id}`} className="text-primary hover:underline">{quote.Client.name}</Link></p>
                        {quote.Client.type && <p className="text-sm text-gray-600">Тип: {quote.Client.type}</p>}
                        {quote.Client.director && <p className="text-sm text-gray-600">Директор: {quote.Client.director}</p>}
                        {quote.Client.phone && <p className="text-sm text-gray-600">Телефон: {quote.Client.phone}</p>}
                        {quote.Client.email && <p className="text-sm text-gray-600">Email: {quote.Client.email}</p>}
                    </div>
                ) : (
                    <p className="text-gray-500">Информация о клиенте недоступна</p>
                )}
           </div>
      </div>

      {/* Quote Items Table */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Позиции предложения</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Конструкция</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Период</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Кол-во/Дней</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Цена ед.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Скидка</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Итого</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заметка</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quote.QuoteItems && quote.QuoteItems.length > 0 ? (
                quote.QuoteItems.map((item) => {
                  const durationDays = calculateDuration(item.startDate, item.endDate);
                  const quantityDisplay = item.isMonthlyRate === null
                    ? `${item.quantity ?? '-'} ед.`
                    : item.isMonthlyRate
                      ? `${item.quantity ?? '-'} мес.`
                      : (durationDays !== null ? `${durationDays} дн.` : '-');
                  const unitLabel = item.isMonthlyRate === null ? '/услуга' : item.isMonthlyRate ? '/мес' : '/день';
                  const unitTitle = item.isMonthlyRate === null
                    ? 'Цена за услугу'
                    : item.isMonthlyRate
                      ? 'Цена за месяц'
                      : 'Цена за день';
                  const discountAmount = item.totalPrice * (item.discountPercentage / 100);
                  return (
                    <tr key={item.id}>
                      <td className="px-4 py-4 text-sm">
                         <Link to={`/structures/${item.Structure.id}`} className="text-primary hover:underline font-medium">{item.Structure.code}</Link>
                         <div className="text-gray-500 text-xs">{item.Structure.type} - {item.Structure.location}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                         {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </td>
                       <td className="px-4 py-4 text-sm text-gray-700 text-right">
                         <span className="inline-flex items-center text-xs">
                             {item.isMonthlyRate ? <CalendarDaysIcon className="h-3 w-3 mr-1 text-gray-400" /> : <CalendarDaysIcon className="h-3 w-3 mr-1 text-gray-400" />}
                             {quantityDisplay}
                         </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 text-right">
                         {formatCurrency(item.unitPrice)}
                         <span className="ml-1 inline-flex items-center text-xs text-gray-500" title={unitTitle}>
                             ({unitLabel})
                             <InformationCircleIcon className="h-3 w-3 ml-0.5 text-gray-400"/>
                         </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-green-600 text-right">
                         {item.discountPercentage > 0 ? `${item.discountPercentage.toFixed(1)}% (${formatCurrency(discountAmount)})` : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">
                         {formatCurrency(item.finalPrice)}
                      </td>
                       <td className="px-4 py-4 text-xs text-gray-500">
                          {item.notes || '-'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-500">
                    Нет позиций в предложении.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Notes, Terms and Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="md:col-span-2 bg-white rounded-lg shadow p-6 space-y-4">
              {quote.notes && (
                  <div>
                      <h3 className="text-lg font-semibold mb-2">Заметки</h3>
                      <p className="text-gray-700 whitespace-pre-line">{quote.notes}</p>
                  </div>
              )}
               {quote.termsAndConditions && (
                   <div>
                       <h3 className="text-lg font-semibold mb-2">Условия и положения</h3>
                       <p className="text-gray-700 whitespace-pre-line">{quote.termsAndConditions}</p>
                   </div>
               )}
               {(!quote.notes && !quote.termsAndConditions) && (
                   <p className="text-gray-500">Дополнительные заметки и условия отсутствуют.</p>
               )}
          </div>
           <div className="md:col-span-1">
                <div className="bg-white rounded-lg shadow p-6">
                     <h3 className="text-lg font-semibold text-gray-700 mb-2">Итоги по предложению</h3>
                     <div className="space-y-1 text-sm">
                         <div className="flex justify-between"><span className="text-gray-600">Сумма:</span> <span className="font-medium text-gray-800">{formatCurrency(quote.totalAmount)}</span></div>
                         <div className="flex justify-between"><span className="text-gray-600">Скидка:</span> <span className="font-medium text-green-600">- {formatCurrency(quote.discountAmount)}</span></div>
                         <hr className="my-1" />
                         <div className="flex justify-between text-base font-bold"><span className="text-gray-800">Итого:</span> <span className="text-primary">{formatCurrency(quote.finalAmount)}</span></div>
                     </div>
                 </div>
             </div>
      </div>
    </div>
  );
};

export default QuoteDetails;
